import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { INPUT_INFO_SM_NO_BORDER, InputInfoSM } from '@apps-common/components/elements/form'
import { CollapsibleContent, VertSpacerLg } from '@apps-common/components/layout'
import { CompanySelectModal, ModalButton } from '@apps-common/components/modals'
import { CompanyType } from '@apps-common/config/company'
import { commonIcons } from '@apps-common/config/icons'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { ICompanyListModel } from '@apps-common/types/portal/company'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'

interface IProps {
  loading: boolean
  updating: boolean
  step: ModifyUnitActivationStep
  moveStep: (direction: stepDirection) => void
}

export const UnitActivationCompanyForm = ({ loading, updating, step, moveStep }: IProps) => {
  const user = usePortalFirebaseUser()
  const { isMonitoringCentre, isRadionetUser, company: companyName, companyId } = user || {}
  const [autoInitializedCompany, setAutoInitializedCompany] = useState<null | 'mc' | 'bureau'>(null)

  const { register, control, setValue, watch, formState } = useFormContext<IActivationServiceRequestCreateModel>()
  const { errors } = formState
  const [mc, setMc] = useState<ICompanyListModel | null>()
  const [bureau, setBureau] = useState<ICompanyListModel | null>()
  const [tech, setTech] = useState<ICompanyListModel | null>()

  const mCentreNameVal = watch('mCentre')
  const bureauNameVal = watch('bureau')
  const techNameVal = watch('tech')

  useEffect(() => {
    if (!companyName || !companyId) {
      return
    }

    if (isRadionetUser) {
      return
    }

    if (isMonitoringCentre) {
      setMc({
        companyId,
        companyName
      })
      setAutoInitializedCompany('mc')
    } else {
      setBureau({
        companyId,
        companyName
      })
      setAutoInitializedCompany('bureau')
    }
  }, [isMonitoringCentre, isRadionetUser, companyName, companyId])

  useEffect(() => {
    if (!mc && !mCentreNameVal) return

    setValue('mCentreId', mc?.companyId || '', { shouldValidate: true })
    setValue('mCentre', mc?.companyName || '', { shouldValidate: true })
  }, [mc])

  useEffect(() => {
    if (!bureau && !bureauNameVal) return

    setValue('bureauId', bureau?.companyId || '', { shouldValidate: true })
    setValue('bureau', bureau?.companyName || '', { shouldValidate: true })
  }, [bureau])

  useEffect(() => {
    if (!tech && !techNameVal) return

    setValue('techId', tech?.companyId || '', { shouldValidate: true })
    setValue('tech', tech?.companyName || '', { shouldValidate: true })
  }, [tech])

  return (
    <CollapsibleContent
      title='Step 1 | Company'
      defaultOpen={step === ModifyUnitActivationStep.companies}
      disabled={step !== ModifyUnitActivationStep.companies}
      classes='bg-surface border border-border rounded-2xl text-text'
    >
      <div className='grid sm:grid-cols-2 gap-[20px]'>
        {autoInitializedCompany !== 'mc' && (
          <InputInfoSM
            id='mCentreId'
            hideNoEditIcon
            showLabel
            label='Monitoring Centre'
            value={
              <div>
                {(!mCentreNameVal || mCentreNameVal?.length === 0) && (
                  <ModalButton
                    classes={classNames('w-full border-none text-text text-left')}
                    component={CompanySelectModal}
                    componentProps={{
                      subtitle: 'Selection',
                      title: 'Monitoring Centre',
                      companyType: CompanyType.MonitoringCentre
                    }}
                    confirmCallback={(data) => setMc(data)}
                    enterAnimation={modalSlideUpEnterAnimation}
                    leaveAnimation={modalSlideUpLeaveAnimation}
                  >
                    <ButtonRoundedSm
                      type='div'
                      text='Select Monitoring Centre'
                      loading={false}
                      icon={<commonIcons.add />}
                      iconRight
                      className='action-brand !h-[30px] !px-4 md:max-w-[200px]'
                    />
                  </ModalButton>
                )}
                {mCentreNameVal && mCentreNameVal?.length > 0 && (
                  <button
                    type='button'
                    className='flex items-center justify-start gap-[5px] w-full border-none text-text text-left'
                    onClick={() => setMc(null)}
                  >
                    {mCentreNameVal}
                    <commonIcons.close className='text-[1.2rem]' />
                  </button>
                )}
              </div>
            }
            registration={register('mCentre', {
              required: {
                value: true,
                message: 'Monitoring centre required'
              }
            })}
            loading={loading}
            errors={errors?.mCentre}
            className={classNames(INPUT_INFO_SM_NO_BORDER, '!opacity-100')}
          />
        )}

        {autoInitializedCompany !== 'bureau' && (
          <InputInfoSM
            id='bureauId'
            hideNoEditIcon
            showLabel
            label='Bureau'
            value={
              <div>
                {(!bureauNameVal || bureauNameVal?.length === 0) && (
                  <ModalButton
                    classes={classNames('w-full border-none text-text text-left')}
                    component={CompanySelectModal}
                    componentProps={{
                      subtitle: 'Selection',
                      title: 'Bureau',
                      companyType: CompanyType.Bureau
                    }}
                    confirmCallback={(data) => setBureau(data)}
                    enterAnimation={modalSlideUpEnterAnimation}
                    leaveAnimation={modalSlideUpLeaveAnimation}
                  >
                    <ButtonRoundedSm
                      type='div'
                      text='Select Bureau'
                      loading={false}
                      icon={<commonIcons.add />}
                      iconRight
                      className='action-brand !h-[30px] !px-4 md:max-w-[180px]'
                    />
                  </ModalButton>
                )}
                {bureauNameVal && bureauNameVal?.length > 0 && (
                  <button
                    type='button'
                    className='flex items-center justify-start gap-[5px] w-full border-none text-text text-left'
                    onClick={() => setBureau(null)}
                  >
                    {bureauNameVal} <commonIcons.close className='text-[1.2rem]' />
                  </button>
                )}
              </div>
            }
            registration={register('bureau', {
              required: false
            })}
            loading={loading}
            errors={errors?.bureau}
            className={classNames(INPUT_INFO_SM_NO_BORDER, '!opacity-100')}
          />
        )}

        <InputInfoSM
          id='techId'
          hideNoEditIcon
          showLabel
          label='Technician'
          value={
            <div>
              <ModalButton
                classes={classNames('w-full border-none text-text text-left')}
                component={CompanySelectModal}
                componentProps={{
                  subtitle: 'Selection',
                  title: 'Technician',
                  companyType: CompanyType.Technician
                }}
                confirmCallback={(data) => setTech(data)}
                enterAnimation={modalSlideUpEnterAnimation}
                leaveAnimation={modalSlideUpLeaveAnimation}
              >
                {(!techNameVal || techNameVal?.length === 0) && (
                  <ButtonRoundedSm
                    type='div'
                    text='Select Technician'
                    loading={false}
                    icon={<commonIcons.add />}
                    iconRight
                    className='action-brand !h-[30px] !px-4 md:max-w-[180px]'
                  />
                )}
                {techNameVal && techNameVal?.length > 0 && (
                  <button
                    type='button'
                    className='flex items-center justify-start gap-[5px] w-full border-none text-text text-left'
                    onClick={() => setTech(null)}
                  >
                    {techNameVal} <commonIcons.close className='text-[1.2rem]' />
                  </button>
                )}
              </ModalButton>
            </div>
          }
          registration={register('tech', {
            required: false
          })}
          loading={loading}
          errors={errors?.tech}
          className={classNames(INPUT_INFO_SM_NO_BORDER, '!opacity-100')}
        />
      </div>
      <div className='flex items-center justify-end pt-2'>
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Next'
          iconRight
          className='sm:grow-0 action-success mx-2'
          icon={<commonIcons.arrowRight />}
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
